import { z } from 'zod';
import { idParamSchema, paginationQuerySchema, queryBoolean } from './common';
import { cultureEntrySchema } from './culture';
import { reportStatusSchema } from './enums';

/**
 * The report-entry screen edits results in place, so details are addressed by
 * id. Replacing the whole `report_details` array would destroy ids and orphan
 * rows.
 */
const updateReportDetailSchema = z.object({
  id: idParamSchema,
  result: z.string().trim().default(''),
  /** Which reference ranges applied to this result. */
  value_ids: z.array(idParamSchema).default([]),
});

/** Ids whose order is meaningful — each may appear once. */
function orderedIds(noun: string) {
  return z
    .array(idParamSchema)
    .default([])
    .refine((ids) => new Set(ids).size === ids.length, `Each ${noun} can be selected only once`);
}

export const updateReportSchema = z.object({
  report_status: reportStatusSchema.optional(),
  /** Array order is print order. */
  comment_ids: orderedIds('comment'),
  /** Array order is print order. */
  remark_ids: orderedIds('remark'),
  details: z.array(updateReportDetailSchema).default([]),
  /** A culture report's findings. Only a culture report accepts it. */
  culture: cultureEntrySchema.optional(),
  /**
   * The reporting doctor this report is signed by.
   *
   * The only attribution the body may set. `performed_by` and `verified_by` come
   * from the session — a request must not be able to name who performed a test —
   * and this one is refused once the report is completed, by the same lock that
   * refuses result edits.
   *
   * Absent means "leave it alone"; `null` means "clear it". `.optional()` after
   * `.nullable()` is what distinguishes the two.
   */
  consultant_id: idParamSchema.nullable().optional(),
});
export type UpdateReportInput = z.infer<typeof updateReportSchema>;

/**
 * Reports are created as part of patient registration (POST /patients), never
 * standalone, so there is no create-report schema.
 */

/* ------------------------------------------------------------------------- *
 * The worklist
 * ------------------------------------------------------------------------- */

const worklistSortSchema = z.enum([
  'due_asc',
  'due_desc',
  'created_desc',
  'created_asc',
  'patient_asc',
]);
export type WorklistSort = z.infer<typeof worklistSortSchema>;

export const WORKLIST_SORT_LABELS = {
  due_asc: 'Due first',
  due_desc: 'Due last',
  created_desc: 'Newest first',
  created_asc: 'Oldest first',
  patient_asc: 'Patient A–Z',
} as const satisfies Record<WorklistSort, string>;

/** The three worklist tabs, in workflow order. */
export const REPORT_STATUS_TABS = ['registered', 'in_progress', 'completed'] as const;

/**
 * `GET /reports` — the status-segmented worklist.
 *
 * `from`/`to` are inclusive calendar dates in the lab's local timezone; the
 * service widens them to a half-open instant range. They filter `completed_at`
 * on the Completed tab and `created_at` everywhere else, because "completed
 * last week" and "registered last week" are different questions and the
 * Completed tab is only ever asking the first.
 */
/**
 * One character matches a large share of every report the lab ever had — no
 * filter at all, at the cost of reading most of them. The command palette
 * draws the same line.
 */
export const WORKLIST_MIN_SEARCH = 2;

export const reportWorklistQuerySchema = paginationQuerySchema.extend({
  search: z
    .string()
    .trim()
    .min(WORKLIST_MIN_SEARCH, `Search with at least ${WORKLIST_MIN_SEARCH} characters`)
    .optional(),
  status: reportStatusSchema.optional(),
  /** Only reports for patients the signed-in user registered. */
  mine: queryBoolean.optional(),
  /** Only open reports past their due time. */
  overdue: queryBoolean.optional(),
  /** Only reports whose patient still owes money. */
  unpaid: queryBoolean.optional(),
  test_id: idParamSchema.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  sort: worklistSortSchema.default('due_asc'),
});
export type ReportWorklistQuery = z.infer<typeof reportWorklistQuerySchema>;
